import React from 'react';
import { createPortal } from 'react-dom';
import { Image, UserPlus, Archive, Trash2, Palette } from 'lucide-react';
import styles from './BoardMenu.module.scss';

export interface BoardMenuProps {
  open: boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  archiveCount: number;
  showDeleteBoard: boolean;
  onChangeBackground: () => void;
  onChangeTextColor: () => void;
  onShowMembers: () => void;
  onShowArchived: () => void;
  onDeleteBoard: () => void;
}

export default function BoardMenu({
  open,
  onClose,
  triggerRef,
  archiveCount,
  showDeleteBoard,
  onChangeBackground,
  onChangeTextColor,
  onShowMembers,
  onShowArchived,
  onDeleteBoard,
}: BoardMenuProps) {
  if (!open) return null;

  const rect = triggerRef.current?.getBoundingClientRect();
  const style: React.CSSProperties = {
    position: 'fixed',
    top: (rect?.bottom ?? 0) + 4,
    right: Math.max(
      8,
      typeof window !== 'undefined'
        ? window.innerWidth - (rect?.right ?? 0)
        : 0,
    ),
    minWidth: 220,
  };

  return createPortal(
    <>
      <div className={styles.menuBackdrop} onClick={onClose} aria-hidden />
      <div className={styles.menuPanel} style={style} role="menu">
        <button
          type="button"
          className={styles.menuItem}
          onClick={() => {
            onClose();
            onChangeBackground();
          }}
          role="menuitem"
        >
          <Image size={14} />
          Change background
        </button>
        <button
          type="button"
          className={styles.menuItem}
          onClick={() => {
            onClose();
            onChangeTextColor();
          }}
          role="menuitem"
        >
          <Palette size={14} />
          Change color of text
        </button>
        <button
          type="button"
          className={styles.menuItem}
          onClick={() => {
            onClose();
            onShowMembers();
          }}
          role="menuitem"
        >
          <UserPlus size={14} />
          Board members
        </button>
        <button
          type="button"
          className={styles.menuItem}
          onClick={() => {
            onClose();
            onShowArchived();
          }}
          role="menuitem"
        >
          <Archive size={14} />
          Archived items
          {archiveCount > 0 && (
            <span className={styles.archiveBadge}>{archiveCount}</span>
          )}
        </button>
        {showDeleteBoard && (
          <button
            type="button"
            className={styles.menuItem}
            onClick={() => {
              onClose();
              onDeleteBoard();
            }}
            role="menuitem"
          >
            <Trash2 size={14} />
            Delete board
          </button>
        )}
      </div>
    </>,
    document.body,
  );
}
